/**
 * The applied-filter chip's two-tone label, and the delete-glyph sizing that goes with it.
 *
 * ── WHY THIS IS ITS OWN MODULE (VRG-512) ─────────────────────────────────────────────
 *
 * Both lived in `AnalyticsAppliedFilters` and were used only there, which was right until
 * the "View by" control grew a chip-shaped fallback trigger and became the SECOND caller —
 * the extraction trigger in working-conventions §36. Copying eight lines of label markup
 * into the granularity control would have been the cheaper edit and exactly the drift that
 * rule exists to stop: the chip row and its one working chip would have had two independent
 * definitions of what a chip label looks like, and nothing compares them.
 *
 * ⚠️ THAT SECOND CALLER IS GONE — the granularity chip was deleted when the bucket became a
 * per-card control (`AnalyticsCardGranularityControl`), so `AnalyticsAppliedFilters` is once
 * again the only importer. This module stays anyway: re-inlining eight lines to satisfy a
 * one-caller rule is churn, and the extraction also removed a require cycle
 * (`AppliedFilters → GranularityFilter → AppliedFilters`) that the next chip-shaped control
 * would recreate. Under Vite's ESM such a cycle resolves to an `undefined` binding at
 * module-evaluation time rather than an error — the chip renders blank with no warning —
 * which is why the arrangement is worth keeping rather than undoing.
 */
import React from 'react'

import { Box, Typography } from '@mui/material'

/**
 * The field name is the QUALIFIER and the value is the VALUE, so the hierarchy is carried
 * by colour — the same treatment the date field's preset label uses. One type size, because
 * two inside a 28px chip reads ragged.
 */
export const ChipLabel = ({ fieldLabel, valueText }: { fieldLabel: string; valueText: string }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
    <Typography component="span" variant="inherit" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
      {fieldLabel}:
    </Typography>
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      {valueText}
    </Box>
  </Box>
)

/**
 * The delete glyph is a lucide SVG rather than MUI's default `Cancel`, so it takes an
 * explicit box: `fontSize` (which is what `TagChip` sets) does not size an SVG with no
 * font-relative dimensions. Shared so every analytics chip's delete glyph matches exactly
 * rather than each needing its own copy of this one rule.
 */
export const CHIP_SX = { '& .MuiChip-deleteIcon': { width: 14, height: 14 } }
